import React from 'react';

import useIsScrolling from '@/core/features/scroll/use-is-scrolling';

export default function useLongPress({
    element,
    onLongPress,
    timeout = 800,
}: {
    element: Element | null;
    onLongPress: Function;
    timeout?: number;
}) {
    const isScrolling = useIsScrolling();

    const longpressTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const clearLongPressTimeout = React.useCallback(() => {
        if (longpressTimeoutRef.current === null) {
            return;
        }
        clearTimeout(longpressTimeoutRef.current);
        longpressTimeoutRef.current = null;
    }, []);

    const touchstartHandler = React.useCallback(() => {
        clearLongPressTimeout();
        longpressTimeoutRef.current = setTimeout(() => {
            onLongPress();
        }, timeout);
    }, [onLongPress, clearLongPressTimeout, timeout]);

    React.useEffect(() => {
        if (isScrolling) {
            clearLongPressTimeout();
        }
    }, [isScrolling, clearLongPressTimeout]);

    React.useEffect(() => {
        if (!element) {
            return;
        }
        element.addEventListener('touchstart', touchstartHandler);
        element.addEventListener('touchend', clearLongPressTimeout);
        element.addEventListener('scroll', clearLongPressTimeout);
        element.addEventListener('touchmove', clearLongPressTimeout);

        return () => {
            element.removeEventListener('touchstart', touchstartHandler);
            element.removeEventListener('touchend', clearLongPressTimeout);
            element.removeEventListener('scroll', clearLongPressTimeout);
            element.removeEventListener('touchmove', clearLongPressTimeout);
        };
    }, [element, clearLongPressTimeout, touchstartHandler]);
}
